// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ChatMessage.test.tsx
import {
  type AskPunttMessage,
  type AskPunttUserQuestion,
} from "@mg/schemas/src/christo/askPuntt";
import { redactStorageLinks } from "@mg/schemas/src/christo/warholToolReturn";
import { type ReviewFiles } from "@mg/schemas/src/commons";
import {
  ArrowSquareOut,
  Globe,
  ThumbsDown,
  ThumbsUp,
} from "@phosphor-icons/react";
import { Flex, Text, Tooltip } from "@radix-ui/themes";
import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { AgentSteps } from "./AgentSteps";
import { AssistantAttachments } from "./AssistantAttachments";
import { ChatVideoAttachment } from "./ChatVideoAttachment";
import { CitedClaimFooterChip } from "./CitedClaimFooterChip";
import { FileAttachmentButton } from "./FileAttachmentButton";
import { ImageWithLoader } from "./ImageWithLoader";
import { PackCitationsRow } from "./PackCitationsRow";
import { PunttAvatar } from "./PunttAvatar";
import {
  type QuestionAttachment,
  type QuestionFormHandle,
  QuestionForm,
} from "./QuestionForm";
import {
  pairQuestionFormAnswers,
  QuestionFormAnswer,
  type QuestionFormPrompt,
} from "./QuestionFormAnswer";
import { SearchProgress } from "./SearchProgress";
import { StructuredResponse } from "./StructuredResponse";
import { ThinkingState } from "./ThinkingState";

import { useRevealRelatedClaims } from "../../../ask-v2/rail/relatedClaimsReveal";
import {
  AvatarWithInitials,
  AVATAR_SIZE_BIG,
  AVATAR_SIZE_NORMAL,
} from "../../../components/AvatarWithInitials";
import { Expandable } from "../../../components/Expandable";
import {
  KnowledgeRuleCitationDialog,
  knowledgeRuleCitationLabel,
} from "../../../components/KnowledgeRuleCitationDialog";
import { Markdown } from "../../../components/Markdown";
import { PunttThinking } from "../../../components/PunttThinking";
import { type LinkableClaim } from "../../../plugins/rehype-claim-citation";
import { useAnalytics } from "../../../utils/analytics";
import { isPunttGuest } from "../../../utils/auth";
import { type LinkableMemory } from "../../../utils/memoryCitationFields";
import { useSubmitAskPunttFeedback } from "../../../utils/queries/askPuntt";
import {
  CHAT_ATOM_AGENT_COMPONENT_IDS,
  chatAtomAgentInstanceId,
} from "../agentComponents/chatAtomRegistry";
import {
  ChatAtoms,
  type ChatFormDisabledReason,
  collectDrawableChatAtoms,
} from "../agentComponents/ChatAtoms";
import {
  projectStartGreetingBubble,
  visibleChatText,
} from "../agentComponents/visibleChatText";
import {
  MENTION_HIGHLIGHT_CLASS,
  renderMentionSegments,
  resolveMentionActorsForFeedMessage,
} from "../chatUtils";
import {
  dedupeWebSourceCitations,
  isArtworkRegionCitation,
  isKnowledgeRuleCitation,
  isWebSourceCitation,
} from "../utils/commentSourceUtils";
import {
  computeRawDisplaySegments,
  stabilizeDisplaySegments,
} from "../utils/displaySegments";
import { formatFullDateTime } from "../utils/messageDateGrouping";
import { sanitizeStreamingMarkdown } from "../utils/streamingMarkdown";
import { useMessageClaimCitations } from "../utils/useMessageClaimCitations";

/** Short "5:04 PM" timestamp for the feed presentation header. */
function formatFeedTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

interface ChatMessageProps {
  message: AskPunttMessage;
  /** Chat ID for feedback submission */
  chatId?: string;
  /**
   * Ticket review files for this chat's active ticket. When present,
   * video attachments play from the same HLS playlist the ticket uses
   * (`reviewFiles[0].source`) because the raw S3 upload URL is revoked
   * by the ingest pipeline once transcoding finishes.
   */
  reviewFiles?: ReviewFiles[];
  /**
   * Map of attachment S3 key → browser blob URL pointing at the
   * original local file. When present, previews for freshly-uploaded
   * video/image attachments render straight from disk, avoiding the
   * CDN entirely. Only populated for messages sent in the current
   * session; rehydrated messages after a page refresh fall back to
   * the HLS/remote resolution logic.
   */
  localPreviewUrls?: Record<string, string>;
  /** True when the backend is still generating this message */
  isStreaming?: boolean;
  /** True when this is the last assistant message (enables interactive form) */
  isLastAssistant?: boolean;
  /** Called when user submits answers via the multi-question form */
  onSubmitAnswers?: (
    formattedAnswers: string,
    attachments?: QuestionAttachment[],
  ) => void;
  /** Disable the question form (e.g. while sending) */
  formDisabled?: boolean;
  /** Why the active question form is disabled. */
  formDisabledReason?: ChatFormDisabledReason;
  /** Ready files attached outside the inline form, such as a page-level drop. */
  chatLevelFileCount?: number;
  /** Ref forwarded to the active QuestionForm for external submit triggers */
  questionFormRef?: React.Ref<QuestionFormHandle>;
  /** Called when the form's "has any answer" state changes */
  onAnswerChange?: (hasAnswer: boolean) => void;
  /**
   * When true, suppresses the inline QuestionForm render (it's shown in the
   * input area instead). Non-last messages still render read-only as usual.
   */
  hideInlineQuestionForm?: boolean;
  /**
   * When true, this user message is the answer to a prior `userQuestions`
   * form — render QuestionFormAnswer chips instead of the markdown bubble.
   * why: completed state lives in the feed/chat list after submit, not in the composer.
   */
  isQuestionFormAnswer?: boolean;
  /**
   * The questions the preceding turn asked, so the answer card can show the
   * sentence and not only the short header stored on this message.
   */
  questionFormPrompts?: readonly QuestionFormPrompt[];
  /**
   * The next turn already answered this message's question form, so the
   * original form is dropped — the answer card carries the question.
   */
  hideAnsweredQuestionForm?: boolean;
  /**
   * Undo handler for a question-form answer. Kept on the message so the
   * thread can restore the form; the card does not draw the control yet.
   */
  onUndoQuestionFormAnswer?: () => void;
  /**
   * When the Deep review progress bar is live, hide leftover AgentSteps /
   * ThinkingState so "Create Asset" is not competing with the stage spine.
   */
  hideAgentChrome?: boolean;
  /**
   * True when the preceding user message carried file attachments. Forwarded
   * to ThinkingState so the pre-agent read pause shows "Reading attachments..."
   * instead of the generic "Starting up...".
   */
  precedingUserHasAttachments?: boolean;
  /**
   * Visual frame for the message. "chat" (default) is the classic chat layout
   * (right-aligned user bubble, left assistant body). "feed" renders a
   * Slack-style row — left avatar + name + role + timestamp header with a
   * left-aligned body — for the Ask Puntt Projects feed, while keeping all the
   * live machinery (streaming, agent steps, canvas, attachments) intact.
   */
  presentation?: "chat" | "feed";
  /**
   * Render the "AI Reviewer" role label beside Puntt's name in the feed
   * header. Opt-in: the V1 Projects feed is multi-participant, so readers
   * need AI turns attributed; the V2 project page follows the Figma frame
   * (49613:1111), which is Name + time only.
   */
  showFeedRoleLabel?: boolean;
  /** Display name for USER turns in the feed presentation (e.g. the viewer). */
  feedActorName?: string;
  /** Avatar URL for USER turns in the feed presentation. */
  feedActorAvatar?: string | null;
  /**
   * When true in feed presentation, the current viewer's user message gets the
   * white bordered bubble. Other members' messages use the gray bubble.
   */
  feedOwnUserMessage?: boolean;
  /**
   * Project members for the feed presentation, used to resolve mention ids on
   * a user message to display names when highlighting `@Name` chips.
   */
  feedMembers?: {
    _id: string;
    name?: string;
    email?: string;
    avatar?: string | null;
  }[];
  /**
   * Feed-only. When true, the message body is collapsed to a short clamped
   * preview with a "Show more" / "Show less" toggle. Set for every feed message
   * except the most recent (which stays fully expanded as the current focus).
   */
  collapsible?: boolean;
  /**
   * Feed-only. When true (narrow chat column), use an inline avatar+name header
   * instead of a full left avatar column so message text can use full width.
   */
  feedCompactLayout?: boolean;
  /**
   * Feed-only. When false, omit the avatar/name header (continuation in a
   * consecutive-author span). Defaults to true.
   */
  showAuthorHeader?: boolean;
  /**
   * Called when the user clicks a numbered Knowledge Database citation chip
   * under an assistant answer. Parents open / focus the Cited Claims rail.
   */
  onCitationSelect?: (selection: { messageId: string; ruleId: string }) => void;
  /** Currently selected rule id (highlights the matching numbered chip). */
  selectedCitationRuleId?: string | null;
  /** Rendered above the user message body, grouped in the same alignment column. */
  renderBeforeContent?: ReactNode;
  /** Rendered below the user message body, grouped in the same alignment column. */
  renderAfterContent?: ReactNode;
}

function questionFormPromptSignature(
  prompts: readonly QuestionFormPrompt[] | undefined,
): string {
  return (prompts ?? [])
    .map((prompt) => `${prompt.header}\0${prompt.question}`)
    .join("\n");
}

function chatMessageTestIds(messageId: string) {
  return {
    assistant: `chat-message-assistant-${messageId}`,
    user: `chat-message-user-${messageId}`,
    content: `chat-message-content-${messageId}`,
    feedRole: `feed-message-role-${messageId}`,
    textSegment: (segmentIdx: number) =>
      `chat-message-text-segment-${messageId}-${segmentIdx}`,
  } as const;
}

/**
 * Pulls answer labels from QuestionForm's `**Header:** answer` submit format.
 * Falls back to the full content when the message is not in that shape.
 */
export function parseQuestionFormAnswerLabels(content: string): string[] {
  return pairQuestionFormAnswers(content).map((pair) => pair.answer);
}

/** What the sanitize / memo gate below is decided from. */
export type StreamingSegmentGate = {
  /** The THREAD-level flag: this row is the last one and the agent is working. */
  isStreaming: boolean;
  /** The row has settled — `content` is written, so nothing is half-typed. */
  hasContent: boolean;
  isLastSegment: boolean;
  /** The ROW itself still carries live text (`streamingContent`). */
  hasLiveText?: boolean;
};

/**
 * True when a trailing text segment is still being typed, so its raw markdown
 * must be hidden ({@link sanitizeStreamingMarkdown}).
 *
 * Gated on the ROW, not only the thread. `isStreaming` is a MESSAGE-level flag
 * the thread hands to the LAST assistant row alone (`chat.tsx`:
 * `waitingForAI && isLast && role === "assistant"`), so a follow-up sent while
 * a review runs pushes the still-writing row off the tail and turns the flag
 * off for it. That row is unchanged — `streamingContent` keeps arriving — and
 * gated on the thread alone it starts rendering `[label](http...` and open
 * source notes raw ("sanitizes a live row that is no longer the last one").
 * The thread flag is still half the answer: the trailing round can arrive on an
 * agentStep with `streamingContent` empty, and that text is just as half-typed
 * ("sanitizes the trailing agentStep round while streaming with no
 * streamingContent"). Either signal is enough; `hasContent` ends both.
 *
 * Behaviour impact — this WIDENS the gate. Master sanitized on
 * `hasLiveText && isLastSegment` only, so a last row that is streaming with no
 * `streamingContent` rendered raw; it now sanitizes, which is the user-visible
 * change: a trailing round arriving on an `agentStep` no longer flashes
 * `[label](http...` before the token closes.
 */
export function sanitizesStreamingSegment(opts: StreamingSegmentGate): boolean {
  if (opts.hasContent || !opts.isLastSegment) return false;
  return opts.isStreaming || !!opts.hasLiveText;
}

/**
 * True when a text segment should use the memoized Markdown child.
 * Trailing incomplete streaming segments stay on live Markdown so sanitize
 * can update each tick; completed segments skip re-parse. The exact
 * complement of {@link sanitizesStreamingSegment} — a segment whose text this
 * render rewrites must not be served from a memo keyed on the old text.
 */
export function shouldMemoizeMarkdownSegment(
  opts: StreamingSegmentGate,
): boolean {
  return !sanitizesStreamingSegment(opts);
}

/**
 * Memoized markdown for a completed text segment. Content is stable once a
 * segment is no longer the trailing streaming segment, so ReactMarkdown +
 * remark-gfm only re-parse the active trailing segment during a stream.
 */
const MemoizedMarkdownSegment = memo(function MemoizedMarkdownSegment({
  content,
  claimCitations,
  claimCitationScope,
  claimEvidenceScope,
  memoryCitations,
  memoryCitationsFallback,
}: {
  content: string;
  claimCitations?: LinkableClaim[];
  claimCitationScope?: string;
  claimEvidenceScope?: string;
  memoryCitations?: LinkableMemory[];
  memoryCitationsFallback?: boolean;
}) {
  return (
    <Markdown
      size="3"
      iconizeEmoji
      claimCitations={claimCitations}
      claimCitationScope={claimCitationScope}
      claimEvidenceScope={claimEvidenceScope}
      memoryCitations={memoryCitations}
      memoryCitationsFallback={memoryCitationsFallback}
    >
      {content}
    </Markdown>
  );
});

function agentStepsSignature(
  steps: AskPunttMessage["agentSteps"] | undefined,
): string {
  if (!steps?.length) return "";
  return `${steps.length}:${steps[steps.length - 1]?.type ?? ""}:${
    steps[steps.length - 1]?.timestamp ?? ""
  }`;
}

/**
 * A value signature, like the citations below: every poll rebuilds the array,
 * and the atoms land on an answer whose text is already final, so identity
 * would either re-render every tick or swallow the update that draws them.
 */
function componentsSignature(
  components: AskPunttMessage["components"] | undefined,
): string {
  if (!components?.length) return "";
  return components.map((c) => `${c.componentId}:${c.instanceId}`).join(",");
}

/**
 * Only KDB-rule, safe web and pack-artwork variants of `citations` are
 * rendered, so the memo signature keys on the values that affect those rows.
 * A value signature — not identity — because every poll rebuilds the array:
 * comparing references would re-render the whole thread on each tick, while
 * skipping citations entirely would swallow the update that attaches the chips
 * to an answer whose text already finished streaming.
 */
function renderedCitationsSignature(
  citations: AskPunttMessage["citations"],
): string {
  if (!citations?.length) return "";
  // why JSON and not a joined string: quotes and products are free text that can hold the separator, so a join
  // lets "a" + "b:c" and "a:b" + "c" read alike and the memo swallows the poll that moved between them
  return JSON.stringify(
    citations.flatMap((citation) => {
      if (isKnowledgeRuleCitation(citation)) return [["rule", citation.ruleId]];
      if (isWebSourceCitation(citation)) {
        return [["web", citation.sourceId, citation.quote ?? ""]];
      }
      if (isArtworkRegionCitation(citation)) {
        const { page, x, y, w, h } = citation.region;
        // why every displayed field: the chip's heading, its claim counts, the popover lines and the dialog all come
        // from these, so a poll that only changes one of them inside an already-cited box must not be swallowed by
        // the memo.
        return [
          [
            "pack",
            citation.sourceId,
            page,
            x,
            y,
            w,
            h,
            citation.quote ?? "",
            citation.product ?? "",
            citation.fileName,
            citation.label,
            citation.status,
            citation.confidence,
            citation.listKind,
            citation.reason ?? "",
            citation.textEn ?? "",
            citation.cropKey ?? "",
            citation.ean ?? "",
          ],
        ];
      }
      return [];
    }),
  );
}

function composerContextSignature(
  quoted: AskPunttMessage["quotedContexts"],
  comment: AskPunttMessage["commentContexts"],
): string {
  const quotes = (quoted ?? []).map((ctx) => ctx.id).join(",");
  const comments = (comment ?? [])
    .map((ctx) => `${ctx.id}:${ctx.commentId}:${ctx.number}`)
    .join(",");
  return `${quotes}|${comments}`;
}

/** Exported for unit tests — keep in sync with `memo(ChatMessage, …)`. */
export function areChatMessagePropsEqual(
  prev: ChatMessageProps,
  next: ChatMessageProps,
): boolean {
  if (prev.message !== next.message) {
    // Identity preserved by streaming merge for non-active rows; if the
    // object changed, compare the fields that affect render.
    const a = prev.message;
    const b = next.message;
    if (
      a._id !== b._id ||
      a.status !== b.status ||
      a.content !== b.content ||
      a.streamingContent !== b.streamingContent ||
      a.feedback !== b.feedback ||
      a.canvasContent !== b.canvasContent ||
      a.canvasLoading !== b.canvasLoading ||
      a.viewTicketId !== b.viewTicketId ||
      a.contentBlocks !== b.contentBlocks ||
      agentStepsSignature(a.agentSteps) !== agentStepsSignature(b.agentSteps) ||
      renderedCitationsSignature(a.citations) !==
        renderedCitationsSignature(b.citations) ||
      componentsSignature(a.components) !== componentsSignature(b.components) ||
      (a.suggestedResponses?.length ?? 0) !==
        (b.suggestedResponses?.length ?? 0) ||
      (a.searchProgress?.length ?? 0) !== (b.searchProgress?.length ?? 0) ||
      (a.attachments?.length ?? 0) !== (b.attachments?.length ?? 0) ||
      composerContextSignature(a.quotedContexts, a.commentContexts) !==
        composerContextSignature(b.quotedContexts, b.commentContexts)
    ) {
      return false;
    }
  }
  return (
    prev.chatId === next.chatId &&
    prev.reviewFiles === next.reviewFiles &&
    prev.localPreviewUrls === next.localPreviewUrls &&
    prev.isStreaming === next.isStreaming &&
    prev.isLastAssistant === next.isLastAssistant &&
    prev.onSubmitAnswers === next.onSubmitAnswers &&
    prev.formDisabled === next.formDisabled &&
    prev.formDisabledReason === next.formDisabledReason &&
    prev.chatLevelFileCount === next.chatLevelFileCount &&
    prev.questionFormRef === next.questionFormRef &&
    prev.onAnswerChange === next.onAnswerChange &&
    prev.hideInlineQuestionForm === next.hideInlineQuestionForm &&
    prev.isQuestionFormAnswer === next.isQuestionFormAnswer &&
    questionFormPromptSignature(prev.questionFormPrompts) ===
      questionFormPromptSignature(next.questionFormPrompts) &&
    prev.hideAnsweredQuestionForm === next.hideAnsweredQuestionForm &&
    prev.onUndoQuestionFormAnswer === next.onUndoQuestionFormAnswer &&
    prev.hideAgentChrome === next.hideAgentChrome &&
    prev.precedingUserHasAttachments === next.precedingUserHasAttachments &&
    prev.presentation === next.presentation &&
    prev.showFeedRoleLabel === next.showFeedRoleLabel &&
    prev.feedActorName === next.feedActorName &&
    prev.feedActorAvatar === next.feedActorAvatar &&
    prev.feedOwnUserMessage === next.feedOwnUserMessage &&
    prev.feedMembers === next.feedMembers &&
    prev.collapsible === next.collapsible &&
    prev.feedCompactLayout === next.feedCompactLayout &&
    prev.showAuthorHeader === next.showAuthorHeader &&
    prev.onCitationSelect === next.onCitationSelect &&
    prev.selectedCitationRuleId === next.selectedCitationRuleId
    // why: renderBeforeContent / renderAfterContent are ReactNode elements the
    // parent (AskPunttThread) rebuilds inline into a plain `rows[]` on every
    // render, so the references are never stable. Comparing them here can never
    // be true, which would defeat memoization for user messages (re-rendering
    // once per streaming token). They are cheap slot content derived from the
    // same `message` the checks above already cover, so omit them and let the
    // memo bail out on identity like assistant rows (which pass undefined).
  );
}

function ChatMessageInner({
  message,
  chatId,
  reviewFiles,
  localPreviewUrls,
  isStreaming,
  isLastAssistant,
  onSubmitAnswers,
  formDisabled,
  formDisabledReason,
  chatLevelFileCount = 0,
  questionFormRef,
  onAnswerChange,
  hideInlineQuestionForm,
  isQuestionFormAnswer = false,
  questionFormPrompts,
  hideAnsweredQuestionForm = false,
  hideAgentChrome = false,
  precedingUserHasAttachments,
  presentation = "chat",
  showFeedRoleLabel = false,
  feedActorName,
  feedActorAvatar,
  feedOwnUserMessage = false,
  feedMembers,
  collapsible = false,
  feedCompactLayout = false,
  showAuthorHeader = true,
  onCitationSelect,
  selectedCitationRuleId = null,
  renderBeforeContent,
  renderAfterContent,
}: ChatMessageProps) {
  const { t, i18n } = useTranslation("ui");
  const testIds = chatMessageTestIds(message._id);
  const analytics = useAnalytics("ask_puntt_chat");
  const feedbackMutation = useSubmitAskPunttFeedback(chatId ?? "");
  const isCompleted = message.status !== "pending" && !!message.content;
  const showFeedback =
    message.role === "assistant" && isCompleted && !isStreaming && chatId;
  // E2E waits read this to tell a turn still being written from a finished one.
  const dataStreaming =
    message.role === "user" ? undefined : isStreaming ? "true" : "false";

  // Display streamingContent while content is being generated, final content once done
  // why: a reply stored before warhol stopped handing out links still carries
  // a presigned URL, the same as the steps under it (`withoutStorageLinks`).
  const storedContent = message.content || message.streamingContent || "";
  // The stored start message keeps `Review ids` for the skill. The bubble does not.
  const shownUserContent =
    message.role === "user" ? visibleChatText(storedContent) : storedContent;
  const displayContent = useMemo(
    () =>
      redactStorageLinks(
        // Stryker disable next-line ConditionalExpression: removes both mutants on the next line. `-> true` is equivalent -- `role` is only "user" | "assistant", so an assistant row already takes this branch, and a user row's `claimLinks` are always empty because `citations` is assistant-only (`packages/schemas/src/christo/askPuntt.ts:776`), leaving no difference a caller can observe. A user row does reach `displayContent` through `segments` and draw those links in its own bubble (`:786`), so the role branch alone is not what makes the mutant unobservable -- the citations contract is. `-> false` is not equivalent; the disable hides it, and "shows the project-start greeting in the saved language" kills it.
        message.role === "assistant"
          ? projectStartGreetingBubble(storedContent, i18n.language)
          : storedContent,
      ),
    [storedContent, message.role, i18n.language],
  );
  const hasContent = !!message.content;
  // The ROW's own live signal, independent of where it sits in the thread —
  // see `sanitizesStreamingSegment` for why the thread-level `isStreaming`
  // cannot answer for a row a follow-up pushed off the tail.
  const hasLiveText = !!message.streamingContent;
  const showStreamingCursor = isStreaming && !hasContent && hasLiveText;

  // Append-only segment freeze: Path-2 streaming order must not reshuffle when
  // Path-1 contentBlocks arrive (see displaySegments.stabilizeDisplaySegments).
  const segmentsFreezeRef = useRef<{
    messageId: string;
    segments: ReturnType<typeof computeRawDisplaySegments>;
  }>({ messageId: "", segments: [] });

  const segments = useMemo(() => {
    const raw = computeRawDisplaySegments(
      message.agentSteps,
      displayContent,
      message.contentBlocks,
      { ignoreContentBlocks: !!isStreaming && !hasContent },
    );
    if (segmentsFreezeRef.current.messageId !== message._id) {
      segmentsFreezeRef.current = { messageId: message._id, segments: [] };
    }
    const stabilized = stabilizeDisplaySegments(
      segmentsFreezeRef.current.segments,
      raw,
      // This row is live or settled, so a shorter frame is its own writer
      // re-deriving the text (or the settle handing off a shaped `content`
      // that is a prefix of the raw reply) rather than a size-fit cut —
      // see `stabilizeDisplaySegments`'s `allowShrink`.
      //
      // Both arms of this expression are pinned at THIS layer, in
      // `__tests__/ChatMessage.test.tsx`: "hands off to a SHORTER settled
      // answer in the same bubble" (`hasContent`) and "takes a live row's own
      // shorter re-derivation of the streamed text" (`hasLiveText`). The
      // displaySegments suite passes `allowShrink` explicitly, so it pins the
      // helper and cannot falsify this call site — do not delete those two
      // cases as "already covered downstream".
      { allowShrink: hasContent || hasLiveText },
    );
    segmentsFreezeRef.current.segments = stabilized;
    return stabilized;
  }, [
    message._id,
    message.agentSteps,
    displayContent,
    message.contentBlocks,
    isStreaming,
    hasContent,
    hasLiveText,
  ]);
  // A segment that renders no text is null, so the memories are handed to the
  // one body that draws each chip.
  const segmentTexts = useMemo(
    () =>
      segments.map((segment) =>
        segment.type === "text" ? segment.content : null,
      ),
    [segments],
  );

  // The Knowledge Database rule citations attached by the approved_claims_kb
  // tool, in both shapes this message draws them: the deduped chips below, and
  // the same claims as links inside the prose. See the hook for why the two are
  // derived together.
  const {
    ruleCitations: knowledgeRuleCitations,
    relatedCount,
    claimLinks,
    memoryLinks,
    segmentMemoryLinks,
  } = useMessageClaimCitations(message.citations, {
    scope: message._id,
    content: typeof message.content === "string" ? message.content : undefined,
    segments: segmentTexts,
  });
  const [dialogRuleId, setDialogRuleId] = useState<string | null>(null);
  const dialogCitation = knowledgeRuleCitations.find(
    ({ citation }) => citation.ruleId === dialogRuleId,
  )?.citation;
  const guest = isPunttGuest();
  const revealRelatedClaims = useRevealRelatedClaims();
  const webSourceCitations = useMemo(
    () => dedupeWebSourceCitations(message.citations ?? []),
    [message.citations],
  );
  const packCitations = useMemo(
    () => (message.citations ?? []).filter(isArtworkRegionCitation),
    [message.citations],
  );

  const drawableChatAtoms = useMemo(() => {
    const atoms = collectDrawableChatAtoms(
      message.components,
      message.agentSteps,
    );
    if (!hideAnsweredQuestionForm && !isStreaming) return atoms;
    // why: the answer card restates an answered question, and a form drawn
    // mid-stream is not answerable yet — mounting it disabled reads as a
    // finished card.
    return atoms.filter(
      (atom) => atom.componentId !== "ask-puntt.chat-atom.question-form",
    );
  }, [
    message.components,
    message.agentSteps,
    hideAnsweredQuestionForm,
    isStreaming,
  ]);
  const hasDrawnChatAtoms =
    message.role === "assistant" && drawableChatAtoms.length > 0;

  const hasSegments = segments.length > 0;
  // why: a memory whose sentence is not found whole may take its chip at the end of the closest paragraph, but only when the answer is ONE body; across several text segments each render would place one.
  const singleTextSegment =
    segments.filter((segment) => segment.type === "text").length === 1;
  const hasToolSegment = segments.some((segment) => segment.type === "tools");
  const hasSearchProgress = (message.searchProgress?.length ?? 0) > 0;
  const showStandaloneSearchProgress = hasSearchProgress && !hasToolSegment;
  const thinkingComplete = hasContent && !isStreaming;
  // why: leapfrog is the empty-turn placeholder. Once this assistant turn has
  // any body (tokens, tools, search, drawn atoms) the animation competes with
  // real content.
  const hasVisibleTurnContent =
    !!displayContent.trim() ||
    hasSegments ||
    hasSearchProgress ||
    hasDrawnChatAtoms;
  const showThinkingStatus =
    message.role === "assistant" &&
    !hasVisibleTurnContent &&
    !thinkingComplete &&
    (isStreaming || message.status === "pending");

  // Hidden once the turn has any body — tools/search/text own the row.
  const showLeadingThinking =
    message.role === "assistant" &&
    !!isStreaming &&
    !hasVisibleTurnContent &&
    !showStreamingCursor &&
    !showThinkingStatus;

  // Streaming cursor fade-out
  const wasStreamingRef = useRef(showStreamingCursor);
  const [cursorFading, setCursorFading] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(showStreamingCursor);

  useEffect(() => {
    if (wasStreamingRef.current && !showStreamingCursor) {
      setCursorFading(true);
      const timer = setTimeout(() => {
        setCursorVisible(false);
        setCursorFading(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    if (showStreamingCursor) {
      setCursorVisible(true);
      setCursorFading(false);
    }
    wasStreamingRef.current = showStreamingCursor;
  }, [showStreamingCursor]);

  // Progressive structured response reveal
  const [revealedSections, setRevealedSections] = useState(0);
  const structuredRevealedRef = useRef(false);

  useEffect(() => {
    if (
      hasContent &&
      message.structuredContent &&
      !structuredRevealedRef.current
    ) {
      structuredRevealedRef.current = true;
      const timers = [0, 150, 300, 500].map((delay, i) =>
        setTimeout(() => setRevealedSections(i + 1), delay),
      );
      return () => timers.forEach(clearTimeout);
    }
  }, [hasContent, message.structuredContent]);

  // Resolve @tokens to actors so feed user messages render highlighted chips.
  const mentionActors = useMemo(
    () =>
      resolveMentionActorsForFeedMessage(
        message.content,
        message.mentions,
        feedMembers,
        message.mentionEmails,
      ),
    [message.content, message.mentions, message.mentionEmails, feedMembers],
  );

  // Split into text vs. attachments so the feed presentation can clamp only the
  // text while always rendering attachments in full (line-clamping an <img>
  // crops it and strands the "Show more" toggle under a sliced picture).
  const questionAnswerPairs = isQuestionFormAnswer
    ? pairQuestionFormAnswers(message.content ?? "", questionFormPrompts)
    : [];

  const questionFormAnswerContent =
    isQuestionFormAnswer && questionAnswerPairs.length > 0 ? (
      <div
        data-testid="chat-message-question-form-answers"
        className="flex w-full flex-col gap-3"
      >
        {questionAnswerPairs.map((pair, i) => (
          <QuestionFormAnswer
            key={`${pair.question}-${pair.answer}-${i}`}
            question={pair.question}
            answer={pair.answer}
            agentComponentId={chatAtomAgentInstanceId(
              CHAT_ATOM_AGENT_COMPONENT_IDS.questionFormAnswer,
              String(i),
            )}
            data-testid={`chat-message-question-form-answer-${i}`}
          />
        ))}
      </div>
    ) : null;

  const userTextContent = questionFormAnswerContent ? (
    questionFormAnswerContent
  ) : mentionActors.length > 0 ? (
    <p
      data-testid="chat-message-feed-content"
      className="whitespace-pre-wrap text-base text-puntt-neutral-gray-12"
    >
      {renderMentionSegments(shownUserContent, mentionActors).map((seg, i) =>
        seg.type === "mention" ? (
          <span
            key={i}
            data-testid={`chat-message-mention-${seg.id}`}
            className={MENTION_HIGHLIGHT_CLASS}
          >
            {seg.label}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </p>
  ) : (
    <Markdown
      size="3"
      iconizeEmoji
      claimCitations={claimLinks}
      className="text-puntt-neutral-gray-12 [&_strong]:font-semibold [&_strong]:text-puntt-neutral-gray-12"
    >
      {/* why: Markdown calls String methods; a user turn can arrive with content omitted */}
      {shownUserContent}
    </Markdown>
  );

  const userAttachments =
    message.attachments && message.attachments.length > 0 ? (
      <Flex direction="column" gap="2" className="mt-2">
        {message.attachments.map((att, idx) => {
          const mediaUrl =
            att.source || (att.key ? `https://static.puntt.ai/${att.key}` : "");
          return (
            <div key={att.key ?? idx} className="min-w-0">
              {att.type === "image" && mediaUrl && (
                <ImageWithLoader
                  src={(att.key && localPreviewUrls?.[att.key]) || mediaUrl}
                  alt={att.name ?? "Attachment"}
                  href={mediaUrl}
                  testId={`chat-image-attachment-${message._id}-${idx}`}
                />
              )}
              {att.type === "video" && mediaUrl && (
                <ChatVideoAttachment
                  src={
                    (att.key && localPreviewUrls?.[att.key]) ||
                    reviewFiles?.[0]?.source ||
                    mediaUrl
                  }
                  name={att.name}
                />
              )}
              {att.type === "file" && (mediaUrl || att.source) && (
                <FileAttachmentButton
                  url={mediaUrl || att.source || ""}
                  name={att.name}
                />
              )}
            </div>
          );
        })}
      </Flex>
    ) : null;

  const userContent = (
    <>
      {userTextContent}
      {userAttachments}
    </>
  );

  const feedbackButtons = showFeedback ? (
    <Flex gap="1" className="ml-1">
      <button
        data-testid="chat-feedback-thumbs-up"
        onClick={() => {
          analytics.capture("ask_puntt_feedback", {
            feedback: "thumbs_up",
            message_id: message._id,
            chat_id: chatId,
          });
          feedbackMutation.mutate({
            messageId: message._id,
            feedback: "thumbs_up",
          });
        }}
        className={`rounded-md p-1 transition-colors ${
          message.feedback === "thumbs_up"
            ? "bg-puntt-accent-3 text-puntt-accent-11"
            : "text-puntt-neutral-gray-8 hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-11"
        }`}
        aria-label={t("thumbsUp")}
      >
        <ThumbsUp
          size={16}
          weight={message.feedback === "thumbs_up" ? "fill" : "regular"}
        />
      </button>
      <button
        data-testid="chat-feedback-thumbs-down"
        onClick={() => {
          analytics.capture("ask_puntt_feedback", {
            feedback: "thumbs_down",
            message_id: message._id,
            chat_id: chatId,
          });
          feedbackMutation.mutate({
            messageId: message._id,
            feedback: "thumbs_down",
          });
        }}
        className={`rounded-md p-1 transition-colors ${
          message.feedback === "thumbs_down"
            ? "bg-puntt-red-3 text-puntt-red-9"
            : "text-puntt-neutral-gray-8 hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-11"
        }`}
        aria-label={t("thumbsDown")}
      >
        <ThumbsDown
          size={16}
          weight={message.feedback === "thumbs_down" ? "fill" : "regular"}
        />
      </button>
    </Flex>
  ) : null;

  const assistantBody = (
    <div className="min-w-0 space-y-3">
      {/* Interleaved text bubbles and tool step groups */}
      <div data-testid={testIds.content} className="space-y-3">
        {showLeadingThinking && (
          <div
            data-testid={`chat-message-leading-thinking-${message._id}`}
            className="flex items-center"
          >
            <PunttThinking size={20} label={null} />
          </div>
        )}
        {/* Startup/thinking status persists for plain assistant replies. */}
        {showThinkingStatus && !hideAgentChrome && (
          <ThinkingState
            message={message}
            precedingUserHasAttachments={precedingUserHasAttachments}
          />
        )}

        {showStandaloneSearchProgress && (
          <SearchProgress searchProgress={message.searchProgress ?? []} />
        )}

        {(() => {
          const lastToolIdx = segments.reduce(
            (acc, s, i) => (s.type === "tools" ? i : acc),
            -1,
          );
          return segments.map((segment, idx) => {
            // why-not: no `text && hasDrawnChatAtoms -> null` here -- prose is
            // the answer and the atom sits beside it (warhol rule 11). Hiding
            // text on any drawn atom blanked a review's report beside its
            // list card and the amendment beside a memory card (#5414).
            if (segment.type === "tools") {
              if (hideAgentChrome) return null;
              return (
                <div key={`tools-${idx}`} className="space-y-3">
                  <AgentSteps
                    steps={segment.steps}
                    isStreaming={
                      !!isStreaming && !hasContent && idx === lastToolIdx
                    }
                    message={message}
                  />
                  {idx === lastToolIdx &&
                    message.searchProgress &&
                    message.searchProgress.length > 0 && (
                      <SearchProgress searchProgress={message.searchProgress} />
                    )}
                </div>
              );
            }
            // Text segment
            const isLastSegment = idx === segments.length - 1;
            const showCursor =
              isLastSegment && (showStreamingCursor || cursorVisible);
            // While the last segment is still streaming, hide raw markdown
            // syntax for the trailing incomplete token (e.g. a half-typed
            // `[label](http...` link) so the stream only reveals plain label
            // text until the token completes and pops into rendered markdown.
            // Once finalized (`hasContent`), render the untouched markdown.
            // Which signals say "still being typed" — and why the thread-level
            // `isStreaming` is only half of it — is `sanitizesStreamingSegment`.
            const gate = {
              isStreaming: !!isStreaming,
              hasContent,
              isLastSegment,
              hasLiveText,
            };
            const segmentContent = sanitizesStreamingSegment(gate)
              ? sanitizeStreamingMarkdown(segment.content)
              : segment.content;
            // Completed segments get a memoized Markdown child so only the
            // trailing streaming segment re-parses on each tick.
            const memoizeSegment = shouldMemoizeMarkdownSegment(gate);
            return (
              <div key={`text-${idx}`} data-testid={testIds.textSegment(idx)}>
                <div className="text-puntt-neutral-gray-12">
                  {memoizeSegment ? (
                    <MemoizedMarkdownSegment
                      content={segmentContent}
                      claimCitations={claimLinks}
                      claimCitationScope={`${message._id}-${idx}`}
                      claimEvidenceScope={message._id}
                      memoryCitations={segmentMemoryLinks?.[idx]}
                      memoryCitationsFallback={singleTextSegment}
                    />
                  ) : (
                    <Markdown
                      size="3"
                      iconizeEmoji
                      claimCitations={claimLinks}
                      claimCitationScope={`${message._id}-${idx}`}
                      claimEvidenceScope={message._id}
                      memoryCitations={segmentMemoryLinks?.[idx]}
                      memoryCitationsFallback={singleTextSegment}
                    >
                      {segmentContent}
                    </Markdown>
                  )}
                  {showCursor && (
                    <span
                      className={`ml-0.5 inline-block h-4 w-[3px] animate-pulse rounded-full bg-puntt-accent-9 transition-opacity duration-200 motion-reduce:animate-none ${cursorFading ? "opacity-0" : "opacity-100"}`}
                    />
                  )}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {!hasDrawnChatAtoms && feedbackButtons}

      {/* Structured response — only show when final content is set */}
      {hasContent && message.structuredContent && (
        <StructuredResponse
          content={message.structuredContent}
          maxSections={revealedSections}
          messageId={message._id}
        />
      )}

      {/* Knowledge Database rule citations — numbered chips that act like the
          rail's Cited claims cards: focus the rail and open the rule dialog. */}
      {hasContent &&
        (knowledgeRuleCitations.length > 0 || relatedCount > 0) && (
          <div
            className="mt-2 flex flex-wrap items-center gap-2"
            data-testid={`chat-message-citations-${message._id}`}
          >
            <Text
              as="span"
              size="1"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              {t("citedClaimsPanel.title")}
            </Text>
            {knowledgeRuleCitations.map(({ citation, n }, index) => {
              const statement = knowledgeRuleCitationLabel(
                citation,
                t("citedClaimsPanel.citedClaim"),
                { allowQuote: !guest },
              );
              const provenance = claimLinks.find(
                (claim) => claim.ruleId === citation.ruleId,
              );
              // Same as a rail card click: focus the Cited Claims rail when there
              // is one (only the legacy chat passes `onCitationSelect`), and open
              // the rule dialog either way rather than leaving for the registry.
              return (
                <CitedClaimFooterChip
                  key={citation.ruleId}
                  citation={citation}
                  n={n}
                  messageId={message._id}
                  index={index}
                  selected={selectedCitationRuleId === citation.ruleId}
                  statement={statement}
                  provenance={provenance}
                  guest={guest}
                  onSelect={() => {
                    onCitationSelect?.({
                      messageId: message._id,
                      ruleId: citation.ruleId,
                    });
                    setDialogRuleId(citation.ruleId);
                  }}
                />
              );
            })}
            {relatedCount > 0 && revealRelatedClaims ? (
              // A page with a Context pane lists these rows under Related
              // claims; the count takes the reader there.
              <button
                type="button"
                onClick={() => revealRelatedClaims(message._id)}
                aria-label={t("citedClaimsPanel.relatedCountAriaLabel", {
                  count: relatedCount,
                })}
                data-testid={`chat-message-related-claims-${message._id}`}
                className="cursor-pointer rounded-sm bg-transparent p-0 text-xs text-puntt-neutral-gray-11 underline underline-offset-2 [text-decoration-color:color-mix(in_srgb,currentColor_50%,transparent)] hover:text-puntt-neutral-gray-12"
              >
                {t("citedClaimsPanel.relatedCount", { count: relatedCount })}
              </button>
            ) : relatedCount > 0 ? (
              // The registry rows warhol filled the panel with: counted, not
              // numbered, so a chip is always a rule the answer cited.
              <Text
                as="span"
                size="1"
                className="text-puntt-neutral-gray-11"
                data-testid={`chat-message-related-claims-${message._id}`}
              >
                {t("citedClaimsPanel.relatedCount", { count: relatedCount })}
              </Text>
            ) : null}
            {dialogCitation ? (
              <KnowledgeRuleCitationDialog
                citation={dialogCitation}
                open
                onOpenChange={(open) => {
                  if (!open) setDialogRuleId(null);
                }}
                showKnowledgeDatabaseLink={!guest}
                allowSourcePreview={!guest}
              />
            ) : null}
          </div>
        )}

      {/* Verified URLs returned or opened by Warhol's native web search. */}
      {hasContent &&
        message.role === "assistant" &&
        webSourceCitations.length > 0 && (
          <div
            className="mt-2 flex flex-wrap items-center gap-2"
            data-testid={`chat-message-web-sources-${message._id}`}
          >
            <Text
              as="span"
              size="1"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              {t("chatMessage.webSources")}
            </Text>
            {webSourceCitations.map((citation, index) => {
              const host = new URL(citation.sourceId).hostname.replace(
                /^www\./,
                "",
              );
              return (
                <a
                  key={citation.sourceId}
                  href={citation.sourceId}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={citation.quote}
                  aria-label={`${t("chatMessage.openWebSource")}: ${host}`}
                  data-testid={`chat-web-source-${message._id}-${index}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 px-2.5 py-1 text-xs font-medium text-puntt-neutral-gray-12 no-underline transition-colors hover:bg-puntt-neutral-gray-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
                >
                  <Globe
                    size={13}
                    className="text-puntt-accent-11"
                    aria-hidden
                  />
                  <span>{host}</span>
                  <ArrowSquareOut size={12} aria-hidden />
                </a>
              );
            })}
          </div>
        )}

      {/* Pack artwork regions warhol's find_packs returned: each chip opens
          the real PDF at the cited page with the box highlighted. */}
      {/* Stryker disable next-line ConditionalExpression: `-> true` is equivalent. `citations` is assistant-only (`packages/schemas/src/christo/askPuntt.ts:776`), so a user row's packCitations is empty and PackCitationsRow returns null either way. `-> false` is not equivalent; the streaming case kills it. */}
      {hasContent && message.role === "assistant" && (
        <PackCitationsRow messageId={message._id} citations={packCitations} />
      )}

      <AssistantAttachments
        messageId={message._id}
        attachments={message.attachments}
      />

      {/* Chat atoms the agent drew (warhol `render_component`): a card, a
          list, a form. A form takes its answer only on the last assistant
          turn, and the answer is the person's next message, in words. */}
      {hasDrawnChatAtoms && (
        <ChatAtoms
          messageId={message._id}
          atoms={drawableChatAtoms}
          reviewId={message.viewReviewId}
          interactive={!!isLastAssistant}
          disabled={formDisabled}
          disabledReason={formDisabledReason}
          chatLevelFileCount={chatLevelFileCount}
          onSubmitAnswers={onSubmitAnswers}
        />
      )}

      {hasDrawnChatAtoms && feedbackButtons}

      {/* Multi-question form — hidden for the active message (shown in input area); read-only for history.
          why: a card mounted while this turn is still streaming reads as finished. */}
      {hasContent &&
        message.userQuestions &&
        message.userQuestions.length > 0 &&
        !hideInlineQuestionForm &&
        !hideAnsweredQuestionForm &&
        !isStreaming && (
          <QuestionForm
            data-testid="chat-message-question-form"
            ref={isLastAssistant ? questionFormRef : undefined}
            questions={message.userQuestions as AskPunttUserQuestion[]}
            onSubmit={onSubmitAnswers ?? (() => {})}
            disabled={formDisabled}
            chatLevelFileCount={chatLevelFileCount}
            readOnly={!isLastAssistant}
            hideSubmitButton={isLastAssistant && !!questionFormRef}
            onAnswerChange={isLastAssistant ? onAnswerChange : undefined}
          />
        )}
    </div>
  );

  // Feed presentation: a Slack-style row (left avatar + name + role +
  // timestamp) wrapping the SAME live body, so the project feed keeps full
  // fidelity (streaming, agent steps, canvas, attachments) but reads like the
  // project timeline instead of a centered chat bubble. Narrow columns use a
  // compact inline header (or omit it for consecutive same-author spans).
  if (presentation === "feed") {
    const isUser = message.role === "user";
    const name = isUser ? feedActorName || "Member" : "Puntt";
    // why: V2's Figma feed (49613:1111) is Name + time only, so the role label
    // is opt-in rather than default — see showFeedRoleLabel.
    const role = !isUser && showFeedRoleLabel ? "AI Reviewer" : undefined;
    const time = formatFeedTime(message.createdAt);
    const avatar = isUser ? (
      <AvatarWithInitials
        size={feedCompactLayout ? AVATAR_SIZE_NORMAL : AVATAR_SIZE_BIG}
        name={name}
        avatar={feedActorAvatar ?? undefined}
      />
    ) : (
      <PunttAvatar
        size={feedCompactLayout ? AVATAR_SIZE_NORMAL : AVATAR_SIZE_BIG}
      />
    );
    const body = isUser ? (
      isQuestionFormAnswer ? (
        <div className="mt-1 w-full space-y-2" data-testid={testIds.content}>
          {renderBeforeContent}
          {userTextContent}
          {userAttachments}
          {renderAfterContent}
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          {renderBeforeContent}
          <div
            className={
              feedOwnUserMessage
                ? "space-y-2 rounded-xl border border-puntt-neutral-gray-7 bg-base-white px-4 py-3 shadow-[0_2px_3px_rgba(34,34,1,0.06),0_3px_12px_rgba(23,26,36,0.02)]"
                : "space-y-2 rounded-xl bg-puntt-neutral-gray-3 px-4 py-3"
            }
            data-testid={testIds.content}
          >
            {/* Clamp only the text; attachments always render in full below. */}
            {collapsible ? (
              <Expandable
                maxLines={4}
                size="2"
                allowCollapse
                buttonText="Show more"
                collapseButtonText="Show less"
                testId="feed-message"
              >
                {userTextContent}
              </Expandable>
            ) : (
              userTextContent
            )}
            {userAttachments}
          </div>
          {renderAfterContent}
        </div>
      )
    ) : (
      <div className="mt-1">
        {collapsible ? (
          <Expandable
            maxLines={4}
            size="2"
            allowCollapse
            buttonText="Show more"
            collapseButtonText="Show less"
            testId="feed-message"
          >
            {assistantBody}
          </Expandable>
        ) : (
          assistantBody
        )}
      </div>
    );

    if (feedCompactLayout) {
      return (
        <article
          className={
            showAuthorHeader ? "relative mb-4" : "relative mb-2 mt-0.5"
          }
          data-testid={isUser ? testIds.user : testIds.assistant}
          data-role={message.role}
          data-streaming={dataStreaming}
          data-presentation="feed"
          data-feed-compact="true"
          data-author-header={showAuthorHeader ? "true" : "false"}
        >
          {showAuthorHeader ? (
            <div
              className="mb-1 flex min-w-0 items-center gap-2"
              data-testid="feed-message-author-header"
            >
              <div className="shrink-0">{avatar}</div>
              <span className="truncate text-sm font-semibold text-puntt-neutral-gray-12">
                {name}
              </span>
              {role && (
                <span
                  className="shrink-0 text-xs text-puntt-neutral-gray-10"
                  data-testid={testIds.feedRole}
                >
                  {role}
                </span>
              )}
              {time && (
                <Tooltip content={formatFullDateTime(message.createdAt)}>
                  <span
                    className="shrink-0 cursor-default text-xs text-puntt-neutral-gray-9"
                    data-testid="feed-message-timestamp"
                    aria-label={formatFullDateTime(message.createdAt)}
                  >
                    {time}
                  </span>
                </Tooltip>
              )}
            </div>
          ) : null}
          <div className="min-w-0">{body}</div>
        </article>
      );
    }

    return (
      <article
        className="relative mb-4 flex gap-3"
        data-testid={isUser ? testIds.user : testIds.assistant}
        data-role={message.role}
        data-streaming={dataStreaming}
        data-presentation="feed"
      >
        <div
          className="shrink-0 pt-0.5"
          data-testid="feed-message-avatar-column"
        >
          {avatar}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-puntt-neutral-gray-12">
              {name}
            </span>
            {role && (
              <span
                className="text-xs text-puntt-neutral-gray-10"
                data-testid={testIds.feedRole}
              >
                {role}
              </span>
            )}
            {time && (
              <Tooltip content={formatFullDateTime(message.createdAt)}>
                <span
                  className="shrink-0 cursor-default text-xs text-puntt-neutral-gray-9"
                  data-testid="feed-message-timestamp"
                  aria-label={formatFullDateTime(message.createdAt)}
                >
                  {time}
                </span>
              </Tooltip>
            )}
          </div>
          {body}
        </div>
      </article>
    );
  }

  if (message.role === "user") {
    return (
      <div
        className="relative mb-4"
        data-testid={testIds.user}
        data-role="user"
      >
        {isQuestionFormAnswer ? (
          <div
            className="flex w-full flex-col gap-2"
            data-testid={testIds.content}
          >
            {renderBeforeContent}
            {userContent}
            {renderAfterContent}
          </div>
        ) : (
          <Flex justify="end">
            <div className="flex max-w-[70%] flex-col items-end gap-2">
              {renderBeforeContent}
              <div
                className="w-full space-y-2 rounded-xl border border-puntt-neutral-gray-7 bg-base-white px-4 py-3 shadow-[0_2px_3px_rgba(34,34,1,0.06),0_3px_12px_rgba(23,26,36,0.02)]"
                data-testid={testIds.content}
              >
                {userContent}
              </div>
              {renderAfterContent}
            </div>
          </Flex>
        )}
      </div>
    );
  }

  return (
    <div
      className="relative mb-4"
      data-testid={testIds.assistant}
      data-role="assistant"
      data-streaming={dataStreaming}
    >
      {assistantBody}
    </div>
  );
}

export const ChatMessage = memo(ChatMessageInner, areChatMessagePropsEqual);
